import React, { useCallback, useEffect, useState } from 'react';
import { engagementApi, leadApi, xAdsApi } from '../services/api';
import { BySource, LeadRow, SourceFunnel, XPost, XReport } from '../types';
import { fmtDayTime, istToday } from '../lib';
import DateRangeBar from '../components/DateRangeBar';
import NumberStrip, { StripItem } from '../components/NumberStrip';
import StageChips, { Dash } from '../components/StageChips';
import {
  Section, DAYPARTS, Empty, Failed, Loading, Note, Out, PageFrame, picked, Pill, PlatformLeadsCard, Strip, Th, VisitsCell,
  btn, btnTone, td, tdBig, tdOurs, tr, trClick,
} from '../components/PlatformBits';

/** One promoted post: what X delivered next to what the CRM holds for the
 *  people it sent. The join is the link's tag — a visitor from X arrives as
 *  `utm:x/<medium>/<campaign>`, and the post carries the campaign values its
 *  card was tagged with. X has no click id we can resolve, so there is no
 *  finer join than the post. */
interface PostRow {
  key: string;
  post: XPost | null;          // null = X-tagged arrivals that match no post
  tags: string[];
  landed: number;
  tapped: number;
  new_leads: number;
  stages: Record<string, number>;
  visits_scheduled: number;
  visits_conducted: number;
}

const UNMATCHED = 'unmatched';
const X_ADS = 'https://ads.x.com';

const rollUp = (posts: XPost[], sources: SourceFunnel[]): PostRow[] => {
  const blank = (key: string, post: XPost | null): PostRow =>
    ({ key, post, tags: [], landed: 0, tapped: 0, new_leads: 0, stages: {}, visits_scheduled: 0, visits_conducted: 0 });
  const rows = new Map<string, PostRow>(posts.map((p) => [p.promoted_post_id, blank(p.promoted_post_id, p)]));
  sources.filter((s) => s.tag && s.source === 'x').forEach((s) => {
    const post = posts.find((p) => s.campaign != null && (p.utm_campaign === s.campaign || p.utm_aliases.includes(s.campaign)));
    const key = post?.promoted_post_id ?? UNMATCHED;
    if (!rows.has(key)) rows.set(key, blank(key, null));
    const r = rows.get(key)!;
    r.tags.push(s.tag!);
    r.landed += s.opened;
    r.tapped += s.cta_clicked;
    r.new_leads += s.new_leads;
    r.visits_scheduled += s.visits_scheduled;
    r.visits_conducted += s.visits_conducted;
    Object.entries(s.stages).forEach(([st, n]) => { r.stages[st] = (r.stages[st] ?? 0) + n; });
  });
  return Array.from(rows.values());
};

/** The selected row's first cell carries a forest rule on its left edge. */

/** One running-state row: a quiet label column, the values as pills. */
const StateRow: React.FC<{ label: React.ReactNode; wide?: boolean; children: React.ReactNode }> = ({ label, wide, children }) => (
  <tr className={tr}>
    <td className="px-3 py-3 w-[220px] text-[12.5px] font-semibold text-graphite whitespace-nowrap">{label}</td>
    <td className="px-3 py-2.5">
      <div className={`flex flex-wrap items-center gap-y-1.5 text-[12.5px] text-graphite ${wide ? 'gap-x-5' : 'gap-x-2'}`}>{children}</div>
    </td>
  </tr>
);

const usd = (micro: number, digits = 2) => `$${(micro / 1e6).toFixed(digits)}`;
const n = (v: number) => v.toLocaleString('en-IN');

const XPage: React.FC = () => {
  const [range, setRange] = useState({ from: istToday(), to: istToday() });
  const [report, setReport] = useState<XReport | null>(null);
  const [data, setData] = useState<BySource | null>(null);
  const [leads, setLeads] = useState<LeadRow[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setReport(null); setData(null); setLeads(null); setError(null);
    Promise.all([
      xAdsApi.report(range.from, range.to),
      engagementApi.bySource(range.from, range.to, 'hour'),
    ]).then(([rep, bs]) => {
      setReport(rep); setData(bs);
      const tags = rollUp(rep.posts, bs.sources).flatMap((r) => r.tags);
      return leadApi.byFirstTouch(tags, range.from, range.to).then(setLeads);
    }).catch((err: any) => setError(err?.response?.data?.detail || err.message));
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => {
    setRefreshing(true);
    xAdsApi.refresh()
      .then(load)
      .catch((err: any) => setError(`Refresh failed: ${err?.response?.data?.detail || err.message}`))
      .finally(() => setRefreshing(false));
  };

  if (error) return <Failed what="X" error={error} />;

  const rows = report && data ? rollUp(report.posts, data.sources) : [];
  if (selected && !rows.some((r) => r.key === selected)) setSelected(null);
  const shown = selected ? rows.filter((r) => r.key === selected) : rows;
  const selectedRow = selected ? shown[0] : undefined;
  const tot = shown.reduce((a, r) => ({
    spend: a.spend + (r.post?.spend_micro ?? 0), clicks: a.clicks + (r.post?.link_clicks ?? 0),
    pixel: a.pixel + (r.post?.pixel_landing_views ?? 0), landed: a.landed + r.landed, tapped: a.tapped + r.tapped,
    leads: a.leads + r.new_leads, sched: a.sched + r.visits_scheduled, done: a.done + r.visits_conducted,
  }), { spend: 0, clicks: 0, pixel: 0, landed: 0, tapped: 0, leads: 0, sched: 0, done: 0 });

  const strip: StripItem[] = [
    { v: tot.done, k: 'site visits', cls: 'visited' },
    { v: tot.sched, k: 'scheduled', cls: 'gold' },
    { v: tot.leads, k: 'new leads' },
    { v: n(tot.landed), k: 'landed', p: `of ${n(tot.clicks)} clicks` },
    { v: usd(tot.spend), k: 'spend' },
    ...(tot.leads > 0 ? [{ v: usd(tot.spend / tot.leads), k: 'per lead' }] : []),
  ];

  // Time of day: X's clicks and spend are per hour for the whole account; our
  // side is the X-tagged arrivals in that hour. Both ignore the post filter.
  const xTags = new Set(rows.flatMap((r) => r.tags));
  const ourHours = (data?.hours ?? []).filter((h) => xTags.has(h.tag));
  const dayparts = DAYPARTS.map((d) => ({
    label: d.label,
    spend: (report?.by_hour ?? []).filter((h) => d.has(h.hour)).reduce((a, h) => a + h.spend_micro, 0),
    clicks: (report?.by_hour ?? []).filter((h) => d.has(h.hour)).reduce((a, h) => a + h.link_clicks, 0),
    landed: ourHours.filter((h) => d.has(h.hour)).reduce((a, h) => a + h.opened, 0),
    tapped: ourHours.filter((h) => d.has(h.hour)).reduce((a, h) => a + h.cta_clicked, 0),
    leads: ourHours.filter((h) => d.has(h.hour)).reduce((a, h) => a + h.new_leads, 0),
  }));

  const selectedTags = new Set(shown.flatMap((r) => r.tags));
  const leadRows = (leads ?? []).filter((l) => !selected || selectedTags.has(l.acquisition?.src ?? ''));
  const tagToPost = new Map(rows.flatMap((r) => r.tags.map((t) => [t, r] as const)));
  const postName = (r: PostRow) => r.post?.card_name ?? r.post?.post_text?.slice(0, 40) ?? 'Link tag matches no post';
  const account = report?.account_id ?? '';

  return (
    <PageFrame
      title="X"
      badge={{ platform: 'x' }}
      subline={<>
        Bhuvana Group ads account · USD · {report?.synced_at ? <>synced <b className="text-charcoal">{fmtDayTime(report.synced_at)}</b></> : 'sync time unknown'}
        {account && <> · <Out href={`${X_ADS}/accounts/${account}/campaigns_dashboard`}>Open X Ads</Out></>}
      </>}
      action={<button onClick={refresh} disabled={refreshing || !report} className={`${btn} ${btnTone.forest}`}>{refreshing ? 'Refreshing…' : 'Refresh from X'}</button>}
      bar={<DateRangeBar from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} />}
    >
      {!(report && data) && <Loading what="X" />}

      {report && data && (
        <>
          <Strip><NumberStrip items={strip} /></Strip>

          <Section title="Posts" note={selectedRow
            ? <>leads below are from <b className="text-charcoal">{postName(selectedRow)}</b> · <button className="font-semibold text-forest hover:underline" onClick={() => setSelected(null)}>show all</button></>
            : 'click a post to see its leads'}>
            <table className="w-full min-w-[1040px] border-collapse">
              <thead><tr>
                <Th left>Promoted post</Th><Th>Spend</Th><Th>Clicks</Th><Th>Video</Th>
                <Th ours>Landed</Th><Th ours>Tapped</Th><Th ours>New leads</Th>
                <Th ours>Stage</Th><Th ours>Site visits</Th><Th>$ / lead</Th>
              </tr></thead>
              <tbody>
                {rows.length === 0 && <Empty cols={10}>No X spend or X-tagged arrivals in this window.</Empty>}
                {rows.map((r) => {
                  const p = r.post;
                  const untagged = p && (p.utm_source !== 'x' || !p.utm_campaign);
                  const name = postName(r);
                  return (
                    <tr key={r.key} onClick={() => setSelected(selected === r.key ? null : r.key)} className={trClick(selected === r.key)}>
                      <td className={`px-3 py-3 ${picked(selected === r.key)}`}>
                        <div className={`font-semibold text-[13.5px] leading-snug truncate max-w-[340px] ${p ? '' : 'text-brick'}`} title={name}>{name}</div>
                        {p?.post_text && <div className="text-[11.5px] text-graphite mt-0.5 line-clamp-2 max-w-[340px] whitespace-normal" title={p.post_text}>{p.post_text}</div>}
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap text-[11px] text-graphite">
                          {p && <Pill tone={p.entity_status === 'ACTIVE' ? 'ok' : undefined}>{p.entity_status.toLowerCase()}</Pill>}
                          {untagged && <Pill tone="warn">link not tagged for X</Pill>}
                          {p && <span className="num">{n(p.likes)} likes · {n(p.reposts)} reposts</span>}
                          {p && p.entity_status !== 'DELETED' && (
                            <span onClick={(e) => e.stopPropagation()}><Out href={`https://x.com/i/status/${p.tweet_id}`}>post</Out></span>
                          )}
                          {!p && <span>arrived as {r.tags.join(' · ')}</span>}
                        </div>
                      </td>
                      <td className={tdBig}>
                        {p ? <>{usd(p.spend_micro)}<div className="text-[11px] text-graphite font-normal">{n(p.impressions)} impressions</div></> : <Dash />}
                      </td>
                      <td className={`${td} text-right`}>
                        {p?.link_clicks ? <>{n(p.link_clicks)}<div className="text-[11px] text-graphite">{usd(p.spend_micro / p.link_clicks, 3)} each</div></> : <Dash />}
                      </td>
                      <td className={`${td} text-right`}>
                        {p?.video_starts ? <>{n(p.video_starts)}<div className="text-[11px] text-graphite">{n(p.video_50)} half · {n(p.video_100)} full</div></> : <Dash />}
                      </td>
                      <td className={`${td} text-right`}>{r.landed ? n(r.landed) : <Dash />}</td>
                      <td className={`${td} text-right`}>{r.tapped || <Dash />}</td>
                      <td className={tdOurs}>{r.new_leads || <Dash />}</td>
                      <td className="px-3 py-3 text-right">{r.new_leads ? <StageChips stages={r.stages} /> : <Dash />}</td>
                      <VisitsCell done={r.visits_conducted} sched={r.visits_scheduled} />
                      <td className={`${td} text-right`}>{p && r.new_leads ? usd(p.spend_micro / r.new_leads) : <Dash />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Note>Grey columns are X's, from the sync. Green columns match on the link's <b className="text-charcoal">utm_campaign</b> — a post with spend and blank green columns sent visitors we could not tie to it.</Note>
          </Section>

          <Section title="By time of day" note="all posts · IST">
            <table className="w-full min-w-[760px] border-collapse">
              <thead><tr>
                <Th left>Time of day</Th><Th>Spend</Th><Th>Clicks</Th><Th>$ / click</Th>
                <Th ours>Landed</Th><Th ours>Tapped</Th><Th ours>New leads</Th>
              </tr></thead>
              <tbody>
                {dayparts.map((d) => (
                  <tr key={d.label} className={tr}>
                    <td className="px-3 py-3 font-semibold whitespace-nowrap">{d.label}</td>
                    <td className={tdBig}>{usd(d.spend)}</td>
                    <td className={`${td} text-right`}>{n(d.clicks)}</td>
                    <td className={`${td} text-right`}>{d.clicks ? usd(d.spend / d.clicks, 3) : <Dash />}</td>
                    <td className={`${td} text-right`}>{d.landed ? n(d.landed) : <Dash />}</td>
                    <td className={`${td} text-right`}>{d.tapped || <Dash />}</td>
                    <td className={tdOurs}>{d.leads || <Dash />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="X's count vs ours" note={selectedRow ? 'selected post' : 'all posts'}>
            <table className="w-full min-w-[640px] border-collapse">
              <thead><tr><Th left>Step</Th><Th>X says</Th><Th ours>We saw</Th></tr></thead>
              <tbody>
                {[
                  ['Clicked the link', n(tot.clicks), null],
                  ['Reached the site', `${n(tot.pixel)} · X pixel`, n(tot.landed)],
                  ['Tapped a button', 'X is not told', String(tot.tapped)],
                  ['Became a lead', null, String(tot.leads)],
                ].map(([step, theirs, ours]) => (
                  <tr key={step as string} className={tr}>
                    <td className="px-3 py-3 font-semibold">{step}</td>
                    <td className={`${td} text-right text-graphite`}>{theirs ?? <Dash />}</td>
                    <td className={tdOurs}>{ours ?? <Dash />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {report.campaigns.map((c) => (
            <Section key={c.campaign_id} title={report.campaigns.length > 1 ? `Running state · ${c.name}` : 'Running state'} note="change it in X Ads">
              <table className="w-full min-w-[640px] border-collapse">
                <tbody>
                  <StateRow label="Campaign">
                    <span className="font-semibold text-charcoal truncate max-w-[360px]" title={c.name}>{c.name}</span>
                    <Pill tone={c.servable ? 'ok' : 'warn'}>{c.effective_status.toLowerCase()}</Pill>
                    {c.reasons_not_servable.map((r) => <Pill key={r} tone="warn">{r.replace(/_/g, ' ').toLowerCase()}</Pill>)}
                  </StateRow>
                  <StateRow label="Money">
                    <Pill tone={c.funding.able_to_fund ? 'ok' : 'warn'}>{c.funding.able_to_fund ? 'card can fund' : 'card cannot fund'}</Pill>
                    <span>{c.funding.description}</span>
                  </StateRow>
                  {c.reach_7d != null && (
                    <StateRow label="Reach · 7 days">
                      <span className="num font-semibold text-charcoal">{n(c.reach_7d)} people</span>
                      {c.frequency_7d != null && <Pill tone="gold">{c.frequency_7d.toFixed(1)}× each</Pill>}
                    </StateRow>
                  )}
                  {c.line_items.filter((li) => li.targeting.length > 0).map((li) => (
                    <StateRow key={li.id} label={<span className="block truncate max-w-[200px]" title={`${li.name} aims at`}>{li.name} aims at</span>}>
                      {li.targeting.map((t, i) => <Pill key={i}>{t.name.replace(/_/g, ' ').toLowerCase()}</Pill>)}
                    </StateRow>
                  ))}
                  <StateRow label="In X Ads" wide>
                    <Out href={`${X_ADS}/campaign_form/${account}/campaign/${c.campaign_id}`}>Edit campaign</Out>
                    <Out href={`${X_ADS}/accounts/${account}/campaigns_dashboard`}>Breakdowns</Out>
                    <Out href={`${X_ADS}/accounts/${account}/events_manager`}>Pixel events</Out>
                    <Out href={`${X_ADS}/accounts/${account}/billing`}>Billing</Out>
                  </StateRow>
                </tbody>
              </table>
            </Section>
          ))}

          <PlatformLeadsCard
            title={selectedRow ? `Leads from ${postName(selectedRow)}` : 'Leads from X'} leads={leads} rows={leadRows} empty="No lead in this window came from X."
            origin={selected ? undefined : { header: 'Post', of: (l) => { const r = tagToPost.get(l.acquisition?.src ?? ''); return r ? <span title={postName(r)}>{postName(r)}</span> : <Dash />; } }}
          />
        </>
      )}
    </PageFrame>
  );
};

export default XPage;
